import { forwardRef, TextareaHTMLAttributes, useId } from 'react';
import Label from './Label';
import Error from './Error';
import { Skeleton } from '../Elements/Skeletons';
import { Controller, get, useFormState } from 'react-hook-form';
import { clsx } from 'clsx';

import { fieldWidths } from './Form';

export interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  loading?: boolean;
  label?: string;
  description?: string;
  showCharacterCount?: boolean;
  showSegmentCount?: boolean;
  segmentLength?: number;
  width?: keyof typeof fieldWidths;
}

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  function InternalTextAreaField(
    {
      id,
      defaultValue,
      disabled,
      className,
      loading,
      label,
      description,
      showCharacterCount = false,
      showSegmentCount = false,
      segmentLength = 140,
      width = 'full',
      ...props
    },
    ref
  ) {
    const { name } = props;
    const formState = useFormState();
    const error = get(formState.errors, name);
    const defaultId = useId();
    id = id ?? defaultId;
    return (
      <Controller
        name={props.name ?? ''}
        defaultValue={defaultValue}
        disabled={disabled}
        render={({ field }) => (
          <div
            id={id}
            className={clsx('flex flex-col gap-2 w-full', className, fieldWidths[width])}
          >
            <div className="flex justify-between">
              {label ? (
                <Label htmlFor={`${id}::textarea`} description={description}>
                  {label}
                </Label>
              ) : null}
              {!loading && showCharacterCount && props.maxLength && (
                <span>Characters remaining: {props.maxLength - (field.value?.length ?? 0)} </span>
              )}
              {!loading && showSegmentCount && (
                <span>Segments used: {Math.ceil((field.value?.length ?? 0) / segmentLength)}</span>
              )}
            </div>
            {loading ? (
              <Skeleton height={`${(props.rows ?? 4) * 2}em`} />
            ) : (
              <textarea
                {...props}
                value={field.value ?? ''}
                rows={4}
                id={`${id}::textarea`}
                ref={ref}
                disabled={field.disabled}
                onBlur={field.onBlur}
                onChange={field.onChange}
                className={clsx(
                  'relative w-full p-2 border rounded-md dark:text-blue-100 bg-grey-100 border-grey-500 placeholder:text-grey-500 text-grey-1000 dark:placeholder:text-grey-400 dark:bg-blue-900 disabled:bg-transparent dark:border-grey-600 focus:border-blue-500 dark:focus:border-blue-300 focus:outline-none',
                  {
                    'border-red-500 focus-red-300': !!error,
                  }
                )}
              />
            )}
            <Error error={error} />
          </div>
        )}
      />
    );
  }
);
